import { onScopeDispose, toValue, watch } from "vue";
import { useEventListener } from "@vueuse/core";
import { deepActiveElement } from "#ce/utils/focus";
import { allowsFlip, VIEWPORT_PADDING } from "./usePopupPlacement.js";

const POPUP_ROOT_CLASS = "sp-popup";

/**
 * Returns the floating element of the nearest popup `node` renders in, walking the composed tree
 * through slots and shadow hosts. Returns null outside any popup.
 *
 * @param {Node} node
 * @returns {Element | null}
 */
function nearestPopupRoot(node) {
  let current = node;

  while (current) {
    if (current.classList?.contains(POPUP_ROOT_CLASS)) {
      return current;
    }
    current = current.assignedSlot ?? current.parentNode ?? current.host ?? null;
  }

  return null;
}

function scrollPaddingTop() {
  return parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
}

/**
 * Scrolls the window just far enough to show `control` with `VIEWPORT_PADDING` to spare, below the
 * root's `scroll-padding-top`. Settles the popup's own scrollers first. A control past both edges is
 * aligned to the bottom one.
 *
 * @param {Element} control
 */
function revealControl(control) {
  control.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "instant" });

  const rect = control.getBoundingClientRect();
  const bottom = document.documentElement.clientHeight - VIEWPORT_PADDING;
  const top = scrollPaddingTop() + VIEWPORT_PADDING;

  if (rect.bottom > bottom) {
    window.scrollBy({ top: rect.bottom - bottom, behavior: "instant" });
  } else if (rect.top < top) {
    window.scrollBy({ top: rect.top - top, behavior: "instant" });
  }
}

/**
 * Keeps the keyboard-focused control of a locked popup (`--sp-comp-popup-flip: false`) on screen. The
 * popup is fixed, so the browser never scrolls the window to show a control focused inside it past the
 * viewport edge. This scrolls the window for it:
 *   - after each keydown while the popup is open, once the key has moved focus
 *   - when the popup is first positioned in an opening, for focus a host moved in on open
 *
 * Focus outside this popup, or inside a popup nested in it, is left alone, so opening from the anchor
 * does not move the page.
 *
 * @param {object} options
 * @param {import("vue").Ref<HTMLElement|null>} options.popup Floating element ref.
 * @param {import("vue").Ref<boolean>} options.isOpen Whether the floating popup is open.
 * @param {import("vue").Ref<boolean>} options.isPositioned Floating UI's positioned flag.
 */
export function useLockedFocusReveal({ popup, isOpen, isPositioned }) {
  let frame = 0;

  // Floating UI can report `isPositioned` more than once per opening: a position computed while the popup
  // was closed can resolve after it reopens and set the flag back to false. Only the first report in an
  // opening schedules a reveal.
  let revealPending = toValue(isOpen);

  function cancelPendingReveal() {
    cancelAnimationFrame(frame);
    frame = 0;
  }

  function revealFocusedControl() {
    frame = 0;
    const floating = popup.value;
    const focused = deepActiveElement();

    if (!floating || !focused || nearestPopupRoot(focused) !== floating || allowsFlip(floating)) {
      return;
    }

    revealControl(focused);
  }

  // Runs a frame later, once the triggering key's handlers and default action, or a host focusing a
  // control on open, have moved focus.
  function scheduleReveal() {
    cancelPendingReveal();
    frame = requestAnimationFrame(revealFocusedControl);
  }

  function trackOpening(open) {
    revealPending = open;

    if (!open) {
      cancelPendingReveal();
    }
  }

  function revealOnFirstPosition(positioned) {
    if (positioned && revealPending && toValue(isOpen)) {
      revealPending = false;
      scheduleReveal();
    }
  }

  watch(isOpen, trackOpening, { flush: "sync" });
  watch(isPositioned, revealOnFirstPosition, { flush: "post" });

  // Keydown, because focus moving between controls in one shadow root raises no event outside it.
  // Capture phase, because some option handlers stop the keydown from propagating.
  useEventListener(() => (toValue(isOpen) ? document : null), "keydown", scheduleReveal, { capture: true });

  onScopeDispose(cancelPendingReveal);
}
